import upperFirst from "lodash/upperFirst";
import React, {
  memo,
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import { HStack, Spacer, Stack, Text, VStack } from "@chakra-ui/react";
import { MessageThreadFragment, MessageThreadType } from "gql/graphql";
import { useFile } from "modules/providers/file-provider";
import colors from "app/colors";
import { InfoIcon } from "modules/icons";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import useCreateMessage from "modules/hooks/use-create-message";
import useUserTyping from "modules/hooks/use-user-typing";
import ComposeQuillToolbar from "../compose/quill-toolbar/compose-quill-toolbar";
import useQuill from "modules/quill/use-quill";
import ComposeQuotedMessageList from "../compose/compose-quoted-message-list";
import QuillEditor from "../compose/quill-editor";
import ComposeAttachmentList from "../compose/compose-attachment-list";
import ComposeBottomToolbar from "../compose/compose-bottom-toolbar";
import { IQuillEditorHandle } from "../compose/quill-message-composer";
import ComposeResizeInputButton from "../compose/compose-resize-input-button";
import OutOfOfficeNote from "modules/_shared/out-of-office-note";
import { useAuth } from "modules/providers/auth-provider";
import useUserPreference from "modules/hooks/use-user-preference";

interface IProps {
  messageThread: MessageThreadFragment;
  draftId: string;
  onMessageSent?: () => void;
}

const MessageReply = forwardRef<IQuillEditorHandle, IProps>(
  ({ messageThread, draftId, onMessageSent }, ref) => {
    const { me } = useAuth();
    const onTyping = useUserTyping({ messageThreadId: messageThread.id });
    const { uploadFiles, removeLocalFiles } = useFile();
    const { stringValue: enterKeySetting } = useUserPreference({
      userId: me?.id,
      name: "ENTER_KEY_SETTING",
    });

    const [isExpanded, setIsExpanded] = useState(false);

    const { createMessage, getMessageInputFromDraft, isSending } =
      useCreateMessage(messageThread.id);

    const {
      draft,
      isDraftLoaded,
      files,
      error,
      setError,
      createDraft,
      updateMessage,
      canSendMessage,
    } = useMessageThreadDraft({ draftId });

    useEffect(() => {
      createDraft({
        type: messageThread.type,
        draftType: "reply",
      });
    }, [createDraft, messageThread.type]);

    const placeholder = useMemo(() => {
      switch (messageThread.type) {
        case MessageThreadType.Thread:
          return "Click # to add tags or click @ to callout a person or a group";
        case MessageThreadType.Chat:
          return "Type response";
        case MessageThreadType.Task:
          return "Add comment, files, callouts, tags";
        default:
          return "Type a message";
      }
    }, [messageThread.type]);

    const onChange = useCallback(
      (delta: string, plainText: string, lastChar: string) => {
        setError(undefined);
        onTyping(lastChar);
        updateMessage(delta, plainText);
      },
      [onTyping, setError, updateMessage],
    );

    const onFilesAttached = useCallback(
      (files: File[]) => uploadFiles(draftId, files),
      [draftId, uploadFiles],
    );

    //needs to be a ref because onSendMessage uses reset and focus from useQuill
    const onSendMessageRef = useRef<() => void>();

    const {
      containerRef,
      toolbarRef,
      appendText,
      appendMention,
      format,
      insertLink,
      reset,
      focus,
      activeFormats,
      clickedMention,
      closeClickedMention,
    } = useQuill({
      placeholder,
      onChange,
      defaultValue: draft?.delta,
      isReady: isDraftLoaded,
      onEnter:
        enterKeySetting === "send-message"
          ? () => onSendMessageRef.current?.()
          : undefined,
      onCtrlEnter: () => onSendMessageRef.current?.(),
      onFilePaste: onFilesAttached,
      type: messageThread.type,
      isLocked: messageThread.isLocked,
      accessUserIdList: messageThread.accessUserIdList,
      distributionGroupIdList: messageThread.distributionGroupIdList,
    });

    useImperativeHandle(
      ref,
      () => ({
        appendMention,
        appendText,
        focus,
        reset,
      }),
      [appendMention, appendText, focus, reset],
    );

    const onSendMessage = useCallback(async () => {
      if (!canSendMessage || isSending) {
        return;
      }
      try {
        setError(undefined);
        if (!draft) return;

        const messageInput = getMessageInputFromDraft(draft, files);
        if (!messageInput) return;
        reset();
        focus();
        await createDraft(
          {
            type: messageThread.type,
            draftType: "reply",
          },
          true,
        );
        await createMessage({
          messageInput,
          files,
          quotedMessages: draft.quotedMessageList,
        });
        removeLocalFiles(messageInput.fileList?.map((file) => file.id) || []);
        onMessageSent?.();
      } catch (error) {
        setError(error as Error);
      }
    }, [
      canSendMessage,
      createDraft,
      createMessage,
      draft,
      files,
      focus,
      getMessageInputFromDraft,
      isSending,
      messageThread.type,
      onMessageSent,
      removeLocalFiles,
      reset,
      setError,
    ]);

    onSendMessageRef.current = onSendMessage;

    return (
      <Stack pt={1} pb={3} flex={1} minH={isExpanded ? 300 : 0}>
        {error && (
          <HStack bg="rgba(233, 69, 69, 0.05)" px={2} py={1} rounded={8}>
            <InfoIcon boxSize={6} fill={colors.red} />
            <Text fontSize="sm" color={colors.red}>
              {upperFirst(error.message)}
            </Text>
          </HStack>
        )}
        <HStack w="full">
          <ComposeQuillToolbar
            ref={toolbarRef}
            format={format}
            insertLink={insertLink}
            activeFormats={activeFormats}
          />
          <Spacer />
          <ComposeResizeInputButton
            setIsExpanded={setIsExpanded}
            isExpanded={isExpanded}
          />
        </HStack>
        <OutOfOfficeNote draftId={draftId} />
        <VStack
          w="full"
          spacing={0}
          rounded={4}
          borderColor={colors.grayPrimary}
          borderWidth={1}
          flex={isExpanded ? 1 : undefined}
          px={2}
          minH={0}
          _focusWithin={{
            outline: "2px solid",
            outlineColor: colors.blue,
          }}
        >
          <ComposeQuotedMessageList draftId={draftId} />
          <QuillEditor
            containerRef={containerRef}
            focus={focus}
            draftId={draftId}
            clickedMention={clickedMention}
            onClose={closeClickedMention}
            isExpanded={isExpanded}
          />
          <ComposeAttachmentList draftId={draftId} />
        </VStack>
        <ComposeBottomToolbar
          draftId={draftId}
          appendText={appendText}
          appendMention={appendMention}
          onSend={onSendMessage}
          isSending={isSending}
          messageThread={messageThread}
        />
      </Stack>
    );
  },
);

export default memo(MessageReply);
